<template>
  <div
    v-if="project"
    :class="{ 'active-appbar-menu': menuDisplayed }"
    class="project-apps-container"
    @mouseleave="closeProjectMenu"
  >
    <div class="project-apps-menu pt-4">
      <v-text-field
        v-model="moduleMenuSearch"
        autofocus
        class="mx-4"
        clearable
        dense
        filled
        flat
        hide-details
        placeholder="Type here to search"
        prepend-inner-icon="mdi-magnify"
        style="flex: 0"
      ></v-text-field>
      <div v-if="accessibleModules.length > 0" class="modules-section">
        <v-subheader>Modules</v-subheader>

        <div class="px-4 modules-grid">
          <module-tile
            v-for="module in accessibleModules"
            :key="module.id"
            :module="module"
            :project="project"
            @moduleClicked="navigateToProjectModule(module)"
          />
        </div>
      </div>
      <v-spacer />
      <div
        v-if="
          accessibleGeneralApps.length > 0 || $can('access', 'general-apps')
        "
        class="mb-4"
      >
        <v-subheader>General Apps</v-subheader>

        <!-- fixed 4 columns, no scroll -->
        <div class="px-4 general-apps-grid">
          <module-tile
            v-for="module in accessibleGeneralApps"
            :key="module.id"
            :module="module"
            :project="project"
            @moduleClicked="navigateToAntModule(module)"
          />
        </div>
      </div>

      <div
        class="d-flex align-center px-5 py-4 ant-border-top"
        style="background-color: #f2f2f2"
      >
        <v-tooltip bottom style="z-index: 30">
          <template #activator="{ on }">
            <v-icon
              class="mr-2 ant-icon"
              small
              @click="copyProjectId"
              v-on="on"
            >
              mdi-content-copy
            </v-icon>
          </template>
          <span>Copy id</span>
        </v-tooltip>
        <div class="font-italic fs-12">
          {{ project.name }}
        </div>
        <v-spacer />
        <v-tooltip bottom style="z-index: 30">
          <template #activator="{ on }">
            <v-icon
              class="ant-icon"
              dense
              @click="navigateToProjectDashboard"
              v-on="on"
              >mdi-home-outline
            </v-icon>
          </template>
          <span>Home</span>
        </v-tooltip>
        <v-divider class="mx-2" vertical />
        <v-tooltip
          v-if="$can('access', 'project-settings')"
          bottom
          style="z-index: 30"
        >
          <template #activator="{ on }">
            <v-icon
              class="ant-icon"
              dense
              @click="navigateToProjectSettings"
              v-on="on"
              >mdi-cog-outline
            </v-icon>
          </template>
          <span>Settings</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ModuleTile from './Dashboard/ModuleTile';
import { STOCK_MANAGEMENT_LITE } from '@/modules/modules';

export default {
  name: 'ProjectApps',
  components: { ModuleTile },
  props: {
    menuDisplayed: {
      type: Boolean,
      required: true,
    },
  },
  data: () => {
    return {
      moduleMenuSearch: null,
    };
  },
  computed: {
    ...mapGetters(['project', 'authenticatedUser']),

    accessibleModules() {
      if (this.project) {
        let modules = this.project.modules
          ? this.project.modules.filter((module) => module.accessible)
          : [];
        if (this.moduleMenuSearch) {
          return modules.filter((module) =>
            module.name
              .toLowerCase()
              .includes(this.moduleMenuSearch.toLowerCase())
          );
        }
        return modules.sort((a, b) => a.name.localeCompare(b.name));
      }
      return [];
    },
    accessibleGeneralApps() {
      let modules = [];
      if (this.project.generalPermissions) {
        if (this.project.generalPermissions.workflows) {
          modules.push({
            id: '1',
            accessible: 1,
            description: 'ANT Project Workflows',
            icon: 'mdi-source-branch',
            name: 'workflows',
            route: 'workflows',
          });
        }

        if (this.project.generalPermissions.tables) {
          modules.push({
            id: '2',
            accessible: 1,
            description: 'ANT Project Tables',
            icon: 'mdi-table-multiple',
            name: 'tables',
            route: 'tables',
          });
        }

        if (this.project.generalPermissions.sbs) {
          modules.push({
            id: '3',
            accessible: 1,
            description: 'ANT Project Tables',
            icon: 'mdi-lan',
            name: 'SBS dashboard',
            route: 'sbs',
          });
        }

        if (this.project.generalPermissions.labels) {
          modules.push({
            id: '4',
            accessible: 1,
            description: 'ANT Project Labels',
            icon: 'mdi-label-multiple-outline',
            name: 'Labels management',
            route: 'labels',
          });
        }
      }

      return modules.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    closeProjectMenu() {
      this.$emit('closeProjectMenu');
    },
    copyProjectId() {
      navigator.clipboard.writeText(this.project.id);
      this.$store.commit('showNotification', {
        content: `Copied project id to clipboard`,
        color: 'success',
      });
    },
    navigateToProjectModule(module) {
      const path = `/project/${this.project.slug}/module/${module.route}`;

      const shouldForce = [
        `${STOCK_MANAGEMENT_LITE}-NL`,
        `${STOCK_MANAGEMENT_LITE}-DE`,
      ].includes(module.route);

      this.$router.push({ path }).then(() => {
        if (shouldForce) {
          this.$router.go(0);
        }
      });
    },
    navigateToAntModule(module) {
      this.$router.push({
        path: `/project/${this.project.slug}/${module.route}`,
      });
    },
    navigateToProjectDashboard() {
      this.$router.push({ path: `/project/${this.project.slug}` });
      this.$emit('closeProjectMenu');
    },
    navigateToProjectSettings() {
      this.$router.push({
        path: `/project/${this.project.slug}/settings/teams`,
      });
      this.$emit('closeProjectMenu');
    },
  },
};
</script>

<style lang="scss" scoped>
.project-apps-container {
  z-index: 6;
  opacity: 0;
  position: absolute;
  transform: translateY(0) scale(0);
  transition:
    transform 400ms ease,
    opacity 400ms ease 100ms,
    box-shadow 400ms ease;
  width: 550px; /* keep fixed width */
  height: 650px; /* fixed height; inner area will scroll */
  border-radius: 15px;
  overflow: hidden; /* clip inner scrollbars neatly */
  background-color: white;

  &.active-appbar-menu {
    opacity: 1;
    transform: translateY(calc(-100% - 65px)) scale(1);
    box-shadow: rgba(17, 12, 46, 0.15) 0px 48px 100px 0px;
  }

  .project-apps-menu {
    display: flex;
    height: 100%;
    flex-direction: column;
    min-height: 0; /* allow flex children to shrink for scrolling */

    /* the searchable modules list should take remaining space and scroll */
    .modules-section {
      flex: 1 1 auto;
      min-height: 0; /* critical for scrolling inside flex */
      overflow-y: auto;
      padding-bottom: 0.5rem;

      /* responsive grid that wraps into unlimited rows */
      .modules-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: 1rem;
        align-content: start;
      }
    }

    /* keep General Apps and the footer fixed below the scroll area */
    .mb-4 {
      flex: 0 0 auto;
    }
  }
}

/* Fixed 4 columns only for General Apps */
.modules-grid--fixed4 {
  background: rebeccapurple;
  grid-template-columns: repeat(4, 1fr) !important;
}

/* Make ModuleTile fill the grid cell and not force overflow */
:deep(.module-tile) {
  width: 100%;
  min-width: 0;
}

/* Optional: tighter min tile size on very small screens while width stays fixed */
@media (max-width: 420px) {
  .project-apps-container .modules-section .modules-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}

/* keep your existing styles; add these helpers */
.overflow-x-auto {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* spacing between tiles (since we swapped grid -> flex) */
.general-apps-row {
  gap: 0.5rem;
}

/* lock each tile’s width so it stays one row; tweak as you like */
.general-apps-tile {
  width: 140px;
} /* 120–160px works well */

/* remove the old fixed-4 grid override if present */
.modules-grid--fixed4 {
  /* no longer used */
}

.general-apps-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* always 4 apps visible */
  gap: 1rem;
  align-content: start;
  overflow: hidden; /* no scrollbars */
  width: 100%;
  flex: 0 0 auto; /* keep it fixed height under modules */
}

/* make sure each ModuleTile fits neatly */
:deep(.module-tile) {
  width: 100%;
  min-width: 0;
}

/* optional responsive fallback: 2x2 grid on narrow screens */
@media (max-width: 500px) {
  .general-apps-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
